<template>
  <div>
    <canvas class="powerpack-image" ref="canvas"></canvas>
  </div>
</template>

<script>
import {mapGetters, mapState} from "vuex";
import PowerpackDimension from "./PowerpackDimension";
import WheelBarrowBackImage from '../../assets/frame/wheelbarrow_back.png';
import WheelBarrowFrontImage from '../../assets/frame/wheelbarrow_front.png';
import BaseFrameImage from '../../assets/frame/base_frame.png';
import FuelTankImage from '../../assets/frame/fuel_tank_18l.png';
import CoolerImage from '../../assets/frame/cooler.png';
import UpperFrameBackImage from '../../assets/frame/upper_frame_back.png';
import UpperFrameMiddleImage from '../../assets/frame/upper_frame_middle.png';
import UpperFrameFrontImage from '../../assets/frame/upper_frame_front.png';
import { getFromStorage } from "@/helpers/backendStorage";

export default {
  name: "PowerpackImage",

  data() {
    return {
      blueprintModal: false,
      canvas: null,
      images: [],
      layer: 0,
      imgCache: {},
    }
  },

  components: {
    PowerpackDimension,
    WheelBarrowBackImage,
    WheelBarrowFrontImage,
    BaseFrameImage,
    FuelTankImage,
    CoolerImage,
    UpperFrameBackImage,
    UpperFrameMiddleImage,
    UpperFrameFrontImage,
  },

  computed: {
    ...mapState({
      pumpIndex: state => state.build.pump,
      byPumpId: state => state.pumps.byId,
      tankIndex: state => state.build.tank,
      byTankId: state => state.tanks.byId,
      powerpackOptions: state => state.powerpackOptions.all,
      openFrameOptions: state => state.openFrameOptions.all,
      allValves: state => state.valves.all,
      allAdditionalValves: state => state.additionalValves.all,
      activeValve: state => state.build.valves,
      motors: state => state.motors.all,
      motorId: state => state.build.motor,
      totalHeight: state => state.build.dimensions.totalHeight,
      totalLength: state => state.build.dimensions.totalLength,
      totalWidth: state => state.build.dimensions.totalWidth,
    }),
    ...mapGetters({
      dimensions: 'build/getDimensions',
      valves: 'build/valves/getValves',
      activeCircuit: 'build/getCircuit',
      wheels: 'build/getWheels',
      cooler: 'build/getCooler',
      support: 'build/getSupport',
      lid: 'build/getLid',
      tankerFloat: 'build/getTankerFloat',
      electricalBox: 'build/getElectricalBox',
      motorOption: 'build/getMotorOption',
      getPumpRender: 'pumps/getPumpRender'
    }),
    offsetUpY() {
      switch (this.motorOption) {
        case 'mini_powerpack':
          return -69;
        case 'workplace':
          return -35;
        case 'industrial':
          return -35;
      }

      return 0;
    },
    offsetUpX() {
      switch (this.motorOption) {
        case 'mini_powerpack':
          return -3;
        case 'workplace':
          return 0;
        case 'industrial':
          return 0;
      }

      return 0;
    },
    offsetFrontY() {
      switch (this.motorOption) {
        case 'mini_powerpack':
          return 16.5;
        case 'workplace':
          return -10;
        case 'industrial':
          return 10;
      }

      return 0;
    },
    offsetFrontX() {
      switch (this.motorOption) {
        case 'mini_powerpack':
          return -44;
        case 'workplace':
          return 30;
        case 'industrial':
          return -20;
      }

      return 0;
    },
    ownValves() {
      return this.valves.map((v) => {
        let additionalValves = [];
        v.additionalValves.forEach(av => {
          additionalValves.push(this.allAdditionalValves[av]);
        });

        return {
          valve: this.allValves[v.id],
          additionalValves,
        }
      });
    },
    activeValve() {
      return {name: 'not found', image: 'placeholder'};
    },
    activeMotor() {
      for (const motor of this.motors) {
        if (motor.id === this.motorId) {
          return motor;
        }
      }
      return {name: 'not found', image: 'placeholder'};
    },

    activePump() {
      let id = this.pumpIndex > 0 ? this.pumpIndex : 1;
      return this.byPumpId[id];
    },

    activeTank() {
      let id = this.tankIndex > 0 ? this.tankIndex : 1;
      return this.byTankId[id];
    },
  },
  methods: {
    getOpenFrameOptionSelected(key) {
      let option = this.openFrameOptions.find(option => option.type === key);

      if (option === undefined) {
        return false;
      }

      return option.selected;
    },
    setCanvasSize() {
      this.canvas.width = this.canvas.offsetWidth;
      this.canvas.height = this.canvas.offsetWidth / 1.75342465753;
    },
    addImage(url, offsetUp, offsetFront, width, height, x, y) {
      let newImg = true;
      if (this.imgCache[url] === undefined) {
        newImg = false;
        this.imgCache[url] = new Image();
      }

      let img = this.imgCache[url];
      let layer = ++this.layer;
      this.images.push({
        img: img,
        layer: layer,
        offsetUp: offsetUp,
        offsetFront: offsetFront,
        url: url,
        width: width,
        height: height,
        x: x,
        y: y,
      });
      if (!newImg) img.src = url;
    },
    renderImages() {
      let ctx = this.canvas.getContext('2d');
      this.images.sort((a, b) => a.layer - b.layer > 0 ? 1 : -1).forEach((img) => {
        let isLoaded = img.img.complete && img.img.naturalHeight !== 0;

        if (!isLoaded) return;

        let x = img.x ?? 0;
        let y = img.y ?? this.ownValves.length * ((this.canvas.offsetHeight / 720));


        if (img.offsetUp) {
          y += img.offsetUp * ((this.canvas.offsetHeight / 720) * this.offsetUpY);
          x += img.offsetUp * ((this.canvas.offsetWidth / 1080) * this.offsetUpX);
        }

        if (img.offsetFront) {
          y += img.offsetFront * ((this.canvas.offsetHeight / 720) * this.offsetFrontY);
          x += img.offsetFront * ((this.canvas.offsetWidth / 1080) * this.offsetFrontX);
        }

        ctx.drawImage(
            img.img,
            x,
            y,
            img.width ?? this.canvas.offsetWidth,
            img.height ?? this.canvas.offsetHeight
        );
      });
    },
    drawScene() {
      let ctx = this.canvas.getContext('2d');
      ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
      this.renderImages();

      window.requestAnimationFrame(this.drawScene);
    },
    setImages() {
      this.images = [];

      switch (this.motorOption) {
        case 'mini_powerpack':
          this.renderMiniPowerpack();
          break;
        case 'workplace':
          this.renderWorkplacePowerpack();
          break;
        case 'and_pump':
          this.renderMotorPumpPowerpack();
          break;
        case 'open_frame':
          this.renderOpenFramePowerpack();
          break;
        case 'closed_frame':
          this.renderClosedFramePowerpack();
          break;
        case 'industrial':
          this.renderIndustrialPowerpack();
          break;
      }
    },

    renderMiniPowerpack() {
      let manometer = this.powerpackOptions.find((option) => {
        return option.type === 'manometer';
      })

      if (manometer && manometer.selected) {
        this.addImage(
            getFromStorage(manometer.image),
            0,
            0,
            ((this.canvas.offsetWidth / 720) * 90),
            ((this.canvas.offsetWidth / 720) * 90),
            ((this.canvas.offsetHeight / 720) * 20),
            ((this.canvas.offsetHeight / 720) * 20)
        );
      }

      if (this.support) {
        this.addImage(getFromStorage(this.support.image));
      }

      if (this.activeTank) {
        this.addImage(getFromStorage(this.activeTank.image));
      }

      this.addImage(getFromStorage(this.activeCircuit.image));
      this.addImage(getFromStorage(this.activeMotor.image));

      let A03;
      if (this.ownValves.length) {
        let A01 = this.powerpackOptions.find((option) => {
          return option.type === 'A01';
        })
        A03 = this.powerpackOptions.find((option) => {
          return option.type === 'A03';
        })

        if (A01) {
          this.addImage(getFromStorage(A01.image));
        }
      }

      this.ownValves.forEach((entry, i) => {
        if (entry.valve && entry.valve.a10 === null || entry.valve && entry.valve.a10 === 0) {
          if (A03) this.addImage(getFromStorage(A03.image), i);

          entry.additionalValves.forEach((av, j) => {
            this.addImage(getFromStorage(av['3d_render']), i, j);
          })
        }
        if (entry.valve) {
          this.addImage(getFromStorage(entry.valve.image), i, entry.additionalValves.length);
        }
      });
    },

    renderWorkplacePowerpack() {
      if (this.activeTank) {
        this.addImage(getFromStorage(this.activeTank.image));
      }
      if (this.tankerFloat) {
        this.addImage(
            getFromStorage(this.tankerFloat.image),
            0,
            0,
            ((this.canvas.offsetWidth / 720) * 90),
            ((this.canvas.offsetWidth / 720) * 90),
            ((this.canvas.offsetHeight / 720) * 20),
            ((this.canvas.offsetHeight / 720) * 20)
        );
      }
      if (this.wheels) {
        this.addImage(getFromStorage(this.wheels.image));
      }
      if (this.lid) {
        this.addImage(getFromStorage(this.lid.render_image));
      }
      if (this.cooler) {
        this.addImage(getFromStorage(this.cooler.image));
      }
      if (this.electricalBox) {
        this.addImage(getFromStorage(this.electricalBox.image));
      }

      if (this.activeMotor.image !== null) {
        this.addImage(getFromStorage(this.activeMotor.image));
      }

      // VALVES! :D

      if (this.ownValves.length) {
        let bracket = this.powerpackOptions.find((option) => {
          return option.type === 'bracket';
        });
        if (bracket) {
          this.addImage(getFromStorage(bracket.image));
        }

        this.ownValves.forEach((entry, i) => {
          let frontOffset = this.ownValves.length - i - 1;

          if (entry.valve) {
            entry.additionalValves.forEach((av, j) => {
              this.addImage(getFromStorage(av.image), j, frontOffset);
            })
          }
          this.addImage(getFromStorage(entry.valve.image), entry.additionalValves.length, frontOffset);
        });
      }
    },
    renderMotorPumpPowerpack() {
      this.addImage(getFromStorage(this.activeMotor.image));

      const render = this.getPumpRender(this.activePump, this.activeMotor.flange_size);
      if (render) this.addImage(getFromStorage(render.render_image));
    },
    renderOpenFramePowerpack() {
      let baseFrame = this.activeTank.base_frame;
      let wheelBarrow = this.getTankSpecificActiveOption('open_frame_wheels');
      let upperFrame = this.getTankSpecificActiveOption('open_frame_upper');
      let cooler = this.getTankSpecificActiveOption('open_frame_cooler');
      let fuelTank = this.getTankSpecificActiveOption('open_frame_fuel_tank');

      let dynamo = this.openFrameOptions.find((option) => {
        return option.type === 'open_frame_dynamo';
      })

      if (this.getOpenFrameOptionSelected('open_frame_dynamo') === true && dynamo) {
        this.addImage(
            getFromStorage(dynamo.image),
            0,
            0,
            ((this.canvas.offsetWidth / 720) * 90),
            ((this.canvas.offsetWidth / 720) * 90),
            ((this.canvas.offsetHeight / 720) * 20),
            ((this.canvas.offsetHeight / 720) * 20)
        );
      }

      if (wheelBarrow && wheelBarrow.image) {
        this.addImage(getFromStorage(wheelBarrow.image));

        if (wheelBarrow.image) {
          this.addImage(getFromStorage(wheelBarrow.image));
        }
      }

      if (baseFrame && baseFrame.image) {
        this.addImage(getFromStorage(baseFrame.image));
      }

      if (upperFrame && upperFrame.image) {
        this.addImage(getFromStorage(upperFrame.image));
      }

      if (this.activeMotor && this.activeMotor.image) {
        this.addImage(getFromStorage(this.activeMotor.image));
      }

      if (fuelTank && fuelTank.image) {
        this.addImage(getFromStorage(fuelTank.image));
      }

      if (upperFrame && upperFrame.image_two) {
        this.addImage(getFromStorage(upperFrame.image_two));
      }

      if (this.activePump) {
        this.addImage(getFromStorage(this.activePump.pump_render));
      }

      if (this.activeTank && this.activeTank.image) {
        this.addImage(getFromStorage(this.activeTank.image));
      }

      if (cooler && cooler.image) {
        this.addImage(getFromStorage(cooler.image));
      }

      if (upperFrame && upperFrame.image_three) {
        this.addImage(getFromStorage(upperFrame.image_three));
      }

      if (this.ownValves.length) {
        this.ownValves.forEach((entry) => {
          if (entry.valve) {
            this.addImage(getFromStorage(entry.valve.image));
          }
        });
      }
      if (wheelBarrow && wheelBarrow.image_two) {
        this.addImage(getFromStorage(wheelBarrow.image_two));
      }
    },

    renderClosedFramePowerpack() {
      this.addImage(getFromStorage(this.activeMotor.image));
    },

    renderIndustrialPowerpack() {
      if (this.activeTank) {
        this.addImage(getFromStorage(this.activeTank.image));
      }
      if (this.tankerFloat) {
        this.addImage(
            getFromStorage(this.tankerFloat.image),
            0,
            0,
            ((this.canvas.offsetWidth / 720) * 90),
            ((this.canvas.offsetWidth / 720) * 90),
            ((this.canvas.offsetHeight / 720) * 20),
            ((this.canvas.offsetHeight / 720) * 20)
        );
      }
      if (this.lid) {
        this.addImage(getFromStorage(this.lid.render_image));
      } else {
        this.addImage(getFromStorage(this.activeCircuit.image));
        this.addImage(getFromStorage(this.activeMotor.image));
      }

      let A03;
      if (this.ownValves.length) {
        A03 = this.powerpackOptions.find((option) => {
          return option.type === 'A03_industrial';
        })
      }

      this.ownValves.forEach((entry, i) => {
        if (entry.valve && entry.valve.a10 === null || entry.valve && entry.valve.a10 === 0) {
          if (A03) {
            this.addImage(getFromStorage(A03.image), i);
          }

          entry.additionalValves.forEach((av, j) => {
            this.addImage(getFromStorage(av['3d_render']), i, j);
          })
        }
        if (entry.valve) {
          this.addImage(getFromStorage(entry.valve.image), i, entry.additionalValves.length);
        }
      });
    },

    getTankSpecificOption(optionType) {
      const tankSpecificOptions = this.$store.getters['tanks/getTankSpecificOptions'](this.activeTank);

      return this.openFrameOptions.find((option) => {
        return option.type === optionType
            && tankSpecificOptions.includes(option.id);
      });
    },
    getTankSpecificActiveOption(optionType) {
      const option = this.getTankSpecificOption(optionType);

      if (option === undefined || option.selected !== true) {
        return undefined;
      }

      return option;
    },
  },
  mounted() {
    this.canvas = this.$refs.canvas;
    this.setCanvasSize();
    window.addEventListener('resize', this.setCanvasSize);
    window.requestAnimationFrame(this.drawScene);
    setInterval(() => {
      this.setImages();
    }, 100);
  },
  destroyed() {
    window.removeEventListener('resize', this.setCanvasSize);
  }
}
</script>

<style lang="scss">
canvas.powerpack-image {
  width: 100%;
  max-width: 900px;
  margin: 0 auto;
  display: block;
}
</style>
